import { computed, reactive, ref, watch } from 'vue';
import type {
  GeoTiffMapLayer,
  GeoTiffMapLayerConfig,
  GeoTiffMapLayerGroup,
  GeoTiffMapLayerGroupConfig,
  Map
} from '../models';
import { MapCalculationMode, MapLayerType } from '../enums';
import {
  defaultBandStyleFn,
  defaultMapInfoSubTitleFn,
  defaultMapInfoTitleFn,
  defaultMapInfoValueFn,
  getGeoTiffValues
} from '../utils';
import { useMap } from './map.composable';
import WebGLTileLayer from 'ol/layer/WebGLTile';
import { useMapLayer } from './map-layer.composable';
import { useMapInfo } from './map-info.composable';

export const createGeoTiffMapLayer = (config: GeoTiffMapLayerConfig): GeoTiffMapLayer => {
  const bandStyleFn = config.bandStyleFn !== undefined ? config.bandStyleFn : defaultBandStyleFn;
  const rawData = config.rawData;
  const noDataValue = config.rawData.noDataValue || 0;
  const band = config.band;
  const image = rawData.image;

  const layer: GeoTiffMapLayer = {
    mapLayerType: MapLayerType.GEOTIFF,
    type: config.type,
    id: config.id,
    name: config.name,
    data: {
      classification: config.classification,
      unitType: config.unitType,
      unitTypeName: config.unitTypeName,
      colorMap: config.colorMap,
      values: [],
      band,
      normalize: config.normalize,
      interpolate: config.interpolate,
      blob: rawData.blob,
      image,
      noDataValue,
      bandStyleFn
    },
    enabled: config.enabled !== false,
    opacity: Math.min(Math.max(config.opacity === undefined ? 1 : config.opacity, 0), 1),
    extent: config.extent || [],
    loading: true,
    customData: config.customData,
    mapInfo: {
      doNotGenerate: config.mapInfo?.doNotGenerate === true,
      enabled: config.mapInfo?.enabled !== false,
      titleFn: config.mapInfo?.titleFn !== undefined ? config.mapInfo.titleFn : defaultMapInfoTitleFn,
      subtitleFn: config.mapInfo?.subtitleFn !== undefined ? config.mapInfo.subtitleFn : defaultMapInfoSubTitleFn,
      valueFn: config.mapInfo?.valueFn !== undefined ? config.mapInfo.valueFn : defaultMapInfoValueFn,
      colorBar: config.mapInfo?.colorBar === true
    }
  };

  layer.loadingPromise = new Promise((resolve) => {
    getGeoTiffValues(image, band, noDataValue).then((values) => {
      const { reactiveLayer } = useGeoTiffMapLayer(layer);
      reactiveLayer.data.values = values;
      reactiveLayer.loading = false;
      resolve();
    });
  });

  return layer;
};

export const useGeoTiffMapLayer = (layer: GeoTiffMapLayer) => {
  const composedLayer = useMapLayer(layer);
  const reactiveLayer = composedLayer.reactiveLayer as GeoTiffMapLayer;

  const normalize = computed({
    get: () => reactiveLayer.data.normalize,
    set: (val) => (reactiveLayer.data.normalize = val)
  });
  const interpolate = computed({
    get: () => reactiveLayer.data.interpolate,
    set: (val) => (reactiveLayer.data.interpolate = val)
  });

  return {
    ...composedLayer,
    reactiveLayer,
    normalize,
    interpolate
  };
};

export const useGeoTiffMapLayerComponent = (layer: GeoTiffMapLayer) => {
  const composedLayer = useGeoTiffMapLayer(layer);
  const { reactiveLayer, enabled, loading } = composedLayer;
  const {
    reactiveMap,
    calculationMode,
    updateMapInfos,
    getMergedValuesByLayerType,
    getMapInfoByLayerId,
    updateZIndexes
  } = useMap(reactiveLayer.group?.map as Map);

  const sources = computed(() => [{ blob: reactiveLayer.data.blob, nodata: [0, -9999] }]);

  const layerRef = ref<any>(null);
  const geoTiffSourceRef = ref<any>(null);

  const getGeoTiffLayer = () => {
    return layerRef.value?.tileLayer as WebGLTileLayer;
  };

  const updateLayerStyle = async () => {
    if (!layerRef.value || !enabled.value || loading.value) return;

    const geoTiffLayer = getGeoTiffLayer();

    const values =
      calculationMode.value === MapCalculationMode.ISOLATED
        ? reactiveLayer.data.values
        : getMergedValuesByLayerType(reactiveLayer.type);
    const bandStyleFn = reactiveLayer.data.bandStyleFn;
    const style = await bandStyleFn(reactiveLayer as GeoTiffMapLayer, values);
    geoTiffLayer.setStyle(style);
  };

  const updateLayerOpacity = () => {
    if (!layerRef.value || !enabled.value) return;

    const geoTiffLayer = getGeoTiffLayer();
    geoTiffLayer.setOpacity(reactiveLayer.opacity);
  };

  const updateZIndex = () => {
    if (!layerRef.value || !enabled.value) return;

    const geoTiffLayer = getGeoTiffLayer();
    geoTiffLayer.setZIndex(reactiveLayer.group?.zIndex || 0);
  };

  watch(calculationMode, async () => {
    await updateLayerStyle();
    await updateMapInfos();
    updateZIndex();
  });

  watch(
    () => reactiveLayer.data,
    async () => {
      await updateLayerStyle();
      await updateMapInfos();
      updateZIndex();
    },
    { deep: true }
  );

  watch(
    () => reactiveLayer.opacity,
    () => {
      updateLayerOpacity();
      updateZIndex();
    }
  );

  watch(
    () => layerRef.value,
    async () => {
      await updateLayerStyle();
      await updateMapInfos();
      updateLayerOpacity();
      updateZIndex();

      if (layerRef.value && !reactiveMap.firstMapLayerLoaded) {
        reactiveMap.firstMapLayerLoaded = true;
      }
    }
  );

  watch(
    () => reactiveLayer.enabled,
    (enabled) => {
      const mapInfo = getMapInfoByLayerId(reactiveLayer.id);
      if (mapInfo) {
        const composedMapInfo = useMapInfo(mapInfo);
        composedMapInfo.enabled.value = enabled;
      }

      updateZIndexes();
      updateZIndex();
    }
  );

  watch(
    () => reactiveLayer.group?.zIndex || 0,
    () => {
      updateZIndex();
    }
  );

  return {
    ...composedLayer,
    layerRef,
    geoTiffSourceRef,
    sources
  };
};

export const createGeoTiffMapLayerGroup = (config: GeoTiffMapLayerGroupConfig): GeoTiffMapLayerGroup => {
  const subLayers: GeoTiffMapLayer[] = [];

  if (config.mapLayerConfigs && config.mapLayerConfigs.length) {
    config.mapLayerConfigs.forEach((mapLayerConfig) => {
      subLayers.push(createGeoTiffMapLayer(mapLayerConfig));
    });
  }

  const layerGroup = {
    mapLayerType: MapLayerType.GEOTIFF_GROUP,
    id: config.id,
    name: config.name,
    subLayers,
    activeSubLayer: subLayers[0],
    customData: config.customData,
    zIndex: 0
  };

  subLayers.forEach((subLayer) => (subLayer.group = layerGroup));

  return layerGroup;
};

export const useGeoTiffMapLayerGroup = (layerGroup: GeoTiffMapLayerGroup) => {
  const reactiveLayerGroup = reactive(layerGroup);

  const activeSubLayer = computed(() => reactiveLayerGroup.activeSubLayer);

  const addSubLayer = (subLayer: GeoTiffMapLayer) => {
    subLayer.group = layerGroup;
    reactiveLayerGroup.subLayers.push(subLayer);
  };

  const getSubLayer = (subLayerId: string) => {
    return reactiveLayerGroup.subLayers.find((subLayer) => subLayer.id === subLayerId);
  };

  const switchSubLayer = (subLayerId: string) => {
    const subLayer = getSubLayer(subLayerId);

    if (subLayer) {
      reactiveLayerGroup.activeSubLayer = subLayer;
    }
  };

  return {
    reactiveLayerGroup,
    activeSubLayer,
    addSubLayer,
    getSubLayer,
    switchSubLayer
  };
};
